import { Skeleton } from '@mantine/core'
import { ResponsiveLine } from '@nivo/line'

import type { ResponseRateMonth } from '../mockData'

import { chartTheme, formatMonth, formatPercent } from './chartTheme'

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

interface PointTooltipProps {
  point: {
    data: { x: string | number; y: string | number }
    serieColor: string
  }
}

const LineTooltip = ({ point }: PointTooltipProps) => (
  <div className="rounded-lg bg-white px-3 py-2 shadow-md ring-1 ring-gray-200">
    <p className="text-xs font-medium text-gray-500">
      {formatMonth(String(point.data.x))}
    </p>
    <div className="mt-1 flex items-center gap-2">
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: point.serieColor }}
      />
      <span className="text-sm font-medium text-gray-700">
        {formatPercent(Number(point.data.y))}
      </span>
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VoELineChartProps {
  data: ResponseRateMonth[]
  loading?: boolean
}

export const VoELineChart = ({
  data,
  loading = false,
}: VoELineChartProps) => {
  if (loading) {
    return <Skeleton height={300} />
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-[300px] items-center justify-center text-sm text-gray-400">
        No data available
      </div>
    )
  }

  const seriesData = [
    {
      id: 'Response Rate',
      data: data.map((d) => ({ x: d.month, y: d.responseReceivedRate })),
    },
  ]

  return (
    <div className="h-[300px]">
      <ResponsiveLine
        data={seriesData}
        margin={{ top: 20, right: 30, bottom: 40, left: 50 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, max: 100, stacked: false }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 8,
          format: formatMonth,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          tickValues: 5,
          format: (v) => `${v}%`,
        }}
        enableGridX={false}
        enableGridY={false}
        colors={['#1C93D7']}
        lineWidth={2.5}
        pointSize={8}
        pointColor="#ffffff"
        pointBorderWidth={2.5}
        pointBorderColor="#1C93D7"
        enableArea={true}
        areaOpacity={0.08}
        useMesh={true}
        tooltip={LineTooltip}
        theme={chartTheme}
        curve="monotoneX"
      />
    </div>
  )
}
